/**
 * Discharge Readiness Dashboard (demo placeholder)
 *
 * The mirror image of the Care Readiness Dashboard: the coordination steps our
 * AI agents (with occasional human overrides and vendor dispatches) drive as a
 * Hospital at Home episode winds down — equipment retrieval, final visit sign
 * off, med rec, and device deactivation. Static and hardcoded like its sibling;
 * it illustrates the orchestration rather than reflecting live data. Rendered
 * from the active patients list for patients on an active Hospital at Home
 * episode.
 */

import AirIcon from "@mui/icons-material/Air";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import MedicationIcon from "@mui/icons-material/Medication";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";

import ReadinessDashboardShell, {
  AI_CALL,
  AI_PORTAL,
  AI_SMS,
  CoordinationTask,
  HUMAN_OVERRIDE,
  VENDOR_DISPATCH,
} from "./ReadinessDashboardShell";

// The six discharge coordination steps from the mockup, in reading order.
const TASKS: CoordinationTask[] = [
  {
    icon: MedicalServicesIcon,
    title: "DME Pickup Scheduled",
    subtitle: "Hospital bed & associated equipment retrieval",
    channel: VENDOR_DISPATCH,
    complete: true,
    timing: "Scheduled 2:15 PM",
  },
  {
    icon: HomeWorkIcon,
    title: "Final Home Visit Completed",
    subtitle: "Clinician confirms discharge readiness",
    channel: HUMAN_OVERRIDE,
    complete: true,
    timing: "Completed 1:30 PM",
  },
  {
    icon: MonitorHeartIcon,
    title: "RPM Devices Deactivated",
    subtitle: "Remote monitoring transmission stopped following final visit sign-off",
    channel: AI_CALL,
    complete: false,
    timing: "Due by 3:45 PM",
  },
  {
    icon: MedicationIcon,
    title: "Medication Reconciliation",
    subtitle: "Discharge prescriptions confirmed with pharmacy",
    channel: AI_PORTAL,
    complete: true,
    timing: "Completed 1:05 PM",
  },
  {
    icon: Inventory2Icon,
    title: "Clinical Supplies Retrieved",
    subtitle: "Unused kit & sharps container collected",
    channel: AI_SMS,
    complete: false,
    timing: "Due by 3:00 PM",
    declined: "extra wound dressing kit",
  },
  {
    icon: AirIcon,
    title: "Oxygen Equipment Returned",
    subtitle: "O2 concentrator, tanks & tubing collected",
    channel: AI_SMS,
    complete: false,
    timing: "Due by 4:00 PM",
  },
];

export type DischargeReadinessDashboardProps = {
  patientName: string;
  programLabel: string;
  dischargeTarget?: string;
};

export const DischargeReadinessDashboard = ({
  patientName,
  programLabel,
  dischargeTarget = "Today, 4:00 PM",
}: DischargeReadinessDashboardProps) => (
  <ReadinessDashboardShell
    title={`${programLabel} · Discharge Readiness Dashboard`}
    meta={`Patient: ${patientName} · Discharge Target: ${dischargeTarget}`}
    tasks={TASKS}
    footerText={(complete, total) =>
      `${complete} of ${total} complete — remaining steps on track for ${dischargeTarget.replace(/^Today, /, "")} discharge`
    }
  />
);

export default DischargeReadinessDashboard;
